/**
 * app.<domain> hosts the tool suite at its root instead of under /app —
 * every internal link that used to hardcode "/app/..." now builds its path
 * from this instead, so the same build works cleanly on both hostnames.
 * launchpad.<domain> is the same trick for one tool: it hosts the Launchpad
 * pages at its own root (see LAUNCHPAD_BASE below) while every other tool
 * stays reachable at its usual path, so the two subdomains are just two
 * front doors onto the same suite.
 * Computed once: the hostname doesn't change within a session.
 */
import { TOOL_PATHS, toolPath } from "./slugs";
import { EVM_SUBDOMAIN } from "../evm/site";

export const IS_APP_DOMAIN =
  typeof window !== "undefined" && window.location.hostname.startsWith("app.");
export const IS_LAUNCHPAD_DOMAIN =
  typeof window !== "undefined" && window.location.hostname.startsWith("launchpad.");

export const APP_BASE = IS_APP_DOMAIN || IS_LAUNCHPAD_DOMAIN ? "" : "/app";

/** The tool suite's own "home" route — "/" on app.<domain> or launchpad.<domain>, "/app" elsewhere. */
export const APP_HOME = APP_BASE || "/";

/** Where the Launchpad pages live from wherever we are — bare root on
 * launchpad.<domain>, else the usual "<app base>/launchpad". Every internal
 * Launchpad link (see pages/launchpad/common.js) is built from this. */
export const LAUNCHPAD_BASE = IS_LAUNCHPAD_DOMAIN ? "" : `${APP_BASE}/${TOOL_PATHS.launchpad}`;
export const LAUNCHPAD_HOME = LAUNCHPAD_BASE || "/";

/** In-app path of a tool from its id — `${APP_BASE}/<slug>` — for <Link to>
 * and route matching inside the suite. `toolTo("create-spl")` →
 * "/app/create-solana-spl-token" (or "/create-solana-spl-token" on
 * app.<domain>). A nested path passes through: toolTo("launchpad/mine"). */
export const toolTo = (idOrPath) => `${APP_BASE}/${toolPath(idOrPath)}`;

/** The marketing site's root — always "/" when already there, but a full
 * cross-domain URL (stripping the "app."/"launchpad." prefix) when on a
 * dedicated subdomain, since that's a different hostname entirely, not just
 * a different path. */
export const MARKETING_HOME = IS_APP_DOMAIN || IS_LAUNCHPAD_DOMAIN
  ? `${window.location.protocol}//${window.location.hostname.replace(/^(app|launchpad)\./, "")}/`
  : "/";

// No window means build-time prerendering (scripts/prerender.js), which only
// ever renders the production marketing site — so assume its hostname, and
// the prerendered links match what the client computes once it loads.
const HOSTNAME = typeof window !== "undefined" ? window.location.hostname : "solstack.xyz";
const PROTOCOL = typeof window !== "undefined" ? window.location.protocol : "https:";
// The root domain, stripping off whichever dedicated subdomain we might
// already be on — cross-linking from launchpad.<domain> to app.<domain>
// must not nest into app.launchpad.<domain>.
const ROOT_HOSTNAME = HOSTNAME.replace(/^(app|launchpad)\./, "");
// localhost and the workers.dev preview URL don't have a real "app."
// subdomain to cross-link to — only a proper custom domain does.
const HAS_APP_SUBDOMAIN =
  !!HOSTNAME && !HOSTNAME.includes("localhost") && !HOSTNAME.endsWith(".workers.dev");

// Every marketing-site <a> built from APP_ORIGIN, appUrl or launchpadUrl
// carries rel="nofollow": the app hosts' robots.txt keeps search engines out
// (worker/index.js), and a followed link would still invite Google to list
// the blocked URL with no content in place of the tool's landing page.

/** Where the "Enter Solstack" CTA on the marketing site should go: the real
 * app.<domain> subdomain when one actually exists, else the same-origin
 * /app path (localhost, workers.dev previews) so local/preview testing
 * still works without a matching subdomain. */
export const APP_ORIGIN = IS_APP_DOMAIN
  ? `${PROTOCOL}//${HOSTNAME}/`
  : HAS_APP_SUBDOMAIN
    ? `${PROTOCOL}//app.${ROOT_HOSTNAME}/`
    : "/app";

/** A link to a specific tool from the marketing site — cross-domain to
 * app.<domain> when that subdomain is real, else the same-origin /app/...
 * path. `path` is a tool id or slug, e.g. appUrl("create-spl") and
 * appUrl("create-solana-spl-token") both give .../create-solana-spl-token. */
export function appUrl(path = "") {
  const clean = toolPath(path);
  return APP_ORIGIN.endsWith("/") ? `${APP_ORIGIN}${clean}` : `${APP_ORIGIN}/${clean}`;
}

/** Where the public /launch pages' CTAs should send people into the
 * Launchpad specifically: the real launchpad.<domain> subdomain when one
 * exists, else the same-origin /app/<launchpad slug> path. */
export const LAUNCHPAD_ORIGIN = IS_LAUNCHPAD_DOMAIN
  ? `${PROTOCOL}//${HOSTNAME}/`
  : HAS_APP_SUBDOMAIN
    ? `${PROTOCOL}//launchpad.${ROOT_HOSTNAME}/`
    : `/app/${TOOL_PATHS.launchpad}`;

/** A link to a Launchpad page from outside the app — cross-domain to
 * launchpad.<domain> when real, else the same-origin fallback. `path` is
 * relative, e.g. launchpadUrl("sale/abc") or launchpadUrl() for the home. */
export function launchpadUrl(path = "") {
  const clean = path.replace(/^\//, "");
  if (!clean) return LAUNCHPAD_ORIGIN;
  return LAUNCHPAD_ORIGIN.endsWith("/") ? `${LAUNCHPAD_ORIGIN}${clean}` : `${LAUNCHPAD_ORIGIN}/${clean}`;
}

/** The EVM Dust Cleaner, which runs only on its own host, migration.<domain>
 * (src/evm/site.js): it asks for seed phrases, so no other host serves it.
 * Locally that's the dev server's migration.localhost; a workers.dev
 * preview has no such host, so it links to the live one. */
const PORT = typeof window !== "undefined" && window.location.port ? `:${window.location.port}` : "";
export const EVM_CLEANER_URL = HAS_APP_SUBDOMAIN
  ? `${PROTOCOL}//${EVM_SUBDOMAIN}.${ROOT_HOSTNAME}/`
  : HOSTNAME.includes("localhost")
    ? `${PROTOCOL}//${EVM_SUBDOMAIN}.localhost${PORT}/`
    : `https://${EVM_SUBDOMAIN}.solstack.xyz/`;
